import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import { getRoutePermissions } from '~/modules/common/permissions';
import { useMeContext } from '~/modules/me';
import { getGroupByColumnDisplayName } from '~/modules/resource-management/ResourcingOverview/components/ResourceAvailabilityCard/components/AvailabilityTable/hooks/useColumns';
import { PROBLEM_FILTER_OPTIONS } from '../../ProblemFilterDropdown';
import { FormattedUnitValue, GroupByColumnLabel } from '../columnRenders';
import {
  WorkingHoursHeader,
  FittingAllocationHeader
} from './columnHeaderComponents';

const useStyles = makeStyles(theme => ({
  groupColumn: {
    width: theme.spacing(20),
    minWidth: theme.spacing(20),
    [theme.breakpoints.down('xs')]: {
      minWidth: theme.spacing(1)
    }
  },
  hover: {
    'tbody &:hover': {
      backgroundColor: theme.palette.grey[100]
    }
  },
  numberColumn: {
    width: theme.spacing(15),
    minWidth: theme.spacing(12),
    [theme.breakpoints.down('xs')]: {
      minWidth: theme.spacing(1)
    }
  },
  benchOverallocationValue: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.error.main
  },
  label: {
    fontSize: theme.typography.body2.fontSize,
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.caption.fontSize
    }
  }
}));

const useListTableColumns = ({ groupBy, selectedProblem, overviewUnit }) => {
  const classes = useStyles();
  const me = useMeContext();
  const { canViewResourcing } = getRoutePermissions(me);
  const isClickable = canViewResourcing;

  return useMemo(() => {
    const columns = [
      {
        field: 'label',
        value: getGroupByColumnDisplayName(groupBy),
        className: classNames(classes.groupColumn, {
          [classes.hover]: isClickable
        }),
        groupBy,
        required: true,
        fixed: true,
        visible: true,
        isClickable,
        sortable: false,
        render: GroupByColumnLabel
      },
      {
        field: 'totalResources',
        value: (
          <FormattedMessage id="resourceAvailabilityCard.totalResources" />
        ),
        sortable: false,
        visible: true,
        required: true,
        className: classes.numberColumn,
        render: FormattedUnitValue,
        renderClassName: classes.label,
        overviewUnit
      },
      {
        field: 'workingTime',
        value: <WorkingHoursHeader />,
        sortable: false,
        visible: true,
        required: true,
        className: classes.numberColumn,
        render: FormattedUnitValue,
        renderClassName: classes.label,
        overviewUnit
      },
      {
        field: 'fittingAllocation',
        value: <FittingAllocationHeader />,
        sortable: false,
        visible: true,
        required: true,
        className: classes.numberColumn,
        render: FormattedUnitValue,
        renderClassName: classes.label,
        overviewUnit
      },
      selectedProblem === PROBLEM_FILTER_OPTIONS.BENCH
        ? {
            field: 'bench',
            value: (
              <FormattedMessage id="resourceManagementDashboard.benchOverAllocationTracker.table.bench" />
            ),
            sortable: false,
            visible: true,
            required: true,
            className: classes.numberColumn,
            render: FormattedUnitValue,
            renderClassName: classNames(
              classes.label,
              classes.benchOverallocationValue
            ),
            overviewUnit
          }
        : {
            field: 'overAllocation',
            value: (
              <FormattedMessage id="resourceManagementDashboard.benchOverAllocationTracker.table.overAllocation" />
            ),
            sortable: false,
            visible: true,
            required: true,
            className: classes.numberColumn,
            render: FormattedUnitValue,
            renderClassName: classNames(
              classes.label,
              classes.benchOverallocationValue
            ),
            overviewUnit
          }
    ];

    return {
      listColumns: columns.reduce(
        (obj, item) => ({
          ...obj,
          [item.field]: item
        }),
        {}
      ),
      headers: [
        {
          id: 'top',
          columns: columns.map(({ field, fixed, sortable, className }) => ({
            column: field,
            fixed: fixed || false,
            sortable: sortable || false,
            className
          }))
        }
      ]
    };
  }, [classes, groupBy, isClickable, selectedProblem, overviewUnit]);
};

export default useListTableColumns;
